import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { FileText, Download, Calendar as CalendarIcon, Ship, AlertTriangle, Flame } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const ReportsPage = () => {
    const [convoys, setConvoys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [report, setReport] = useState(null);
    const { hasRole } = useAuth();

    const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());
    const [selectedConvoy, setSelectedConvoy] = useState('');

    useEffect(() => {
        fetchConvoys();
    }, []);

    const fetchConvoys = async () => {
        try {
            const response = await axios.get(`${API}/convoys`);
            setConvoys(response.data);
        } catch (error) {
            toast.error('Erro ao carregar comboios');
        } finally {
            setLoading(false);
        }
    };

    const generateReport = async () => {
        setGenerating(true);
        try {
            const params = new URLSearchParams({
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd')
            });
            
            if (selectedConvoy) {
                params.append('comboio_id', selectedConvoy);
            }

            const response = await axios.get(`${API}/reports/weekly?${params}`);
            setReport(response.data);
            toast.success('Relatório gerado!');
        } catch (error) {
            toast.error('Erro ao gerar relatório');
        } finally {
            setGenerating(false);
        }
    };

    const exportToCSV = () => {
        if (!report) return;

        let csv = 'Relatório Semanal da Frota\n';
        csv += `Período: ${report.periodo.inicio} a ${report.periodo.fim}\n\n`;
        
        csv += 'RESUMO\n';
        csv += `Total ROOs,${report.resumo.total_roos}\n`;
        csv += `ROOs Encerrados,${report.resumo.roos_encerrados}\n`;
        csv += `Total Sinistros,${report.resumo.total_sinistros}\n`;
        csv += `Total PAs,${report.resumo.total_pas}\n\n`;

        csv += 'ROOs\n';
        csv += 'Código,Título,Criticidade,Status,Operador\n';
        report.roos.forEach(roo => {
            csv += `${roo.codigo},${roo.titulo},${roo.criticidade},${roo.status},${roo.operador_nome}\n`;
        });

        csv += '\nSINISTROS\n';
        csv += 'Código,Título,Tipo,Status,Operador\n';
        report.sinistros.forEach(sin => {
            csv += `${sin.codigo},${sin.titulo},${sin.tipo},${sin.status},${sin.operador_nome}\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_${report.periodo.inicio}_${report.periodo.fim}.csv`;
        link.click();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="reports-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                    Relatório Semanal da Frota
                </h1>
                <p className="text-sm text-slate-400 mt-1">
                    Consolidação de dados operacionais por período
                </p>
            </div>

            {/* Filters */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardHeader className="border-b border-slate-800 p-4">
                    <CardTitle className="font-heading text-lg text-white">
                        Filtros do Relatório
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Data Início
                            </Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        data-testid="btn-start-date"
                                        className="w-full justify-start text-left bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {format(startDate, 'dd/MM/yyyy', { locale: ptBR })}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                                    <Calendar
                                        mode="single"
                                        selected={startDate}
                                        onSelect={(date) => date && setStartDate(date)}
                                        locale={ptBR}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Data Fim
                            </Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        data-testid="btn-end-date"
                                        className="w-full justify-start text-left bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {format(endDate, 'dd/MM/yyyy', { locale: ptBR })}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        onSelect={(date) => date && setEndDate(date)}
                                        locale={ptBR}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Comboio (opcional)
                            </Label>
                            <Select 
                                value={selectedConvoy || "all"} 
                                onValueChange={(value) => setSelectedConvoy(value === "all" ? "" : value)}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {convoys.map((convoy) => (
                                        <SelectItem key={convoy.id} value={convoy.id}>
                                            {convoy.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono opacity-0">
                                Ação
                            </Label>
                            <Button
                                onClick={generateReport}
                                data-testid="btn-generate-report"
                                disabled={generating}
                                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                {generating ? 'Gerando...' : 'Gerar Relatório'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Report Results */}
            {report && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className="w-8 h-8 text-amber-400" />
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">ROOs</p>
                                        <p className="text-2xl font-heading font-bold text-white">
                                            {report.resumo.total_roos}
                                        </p>
                                        <p className="text-xs text-emerald-400">
                                            {report.resumo.roos_encerrados} encerrados
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <Flame className="w-8 h-8 text-red-400" />
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">Sinistros</p>
                                        <p className="text-2xl font-heading font-bold text-white">
                                            {report.resumo.total_sinistros}
                                        </p>
                                        <p className="text-xs text-emerald-400">
                                            {report.resumo.sinistros_encerrados} encerrados
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <FileText className="w-8 h-8 text-emerald-400" />
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono uppercase">PAs</p>
                                        <p className="text-2xl font-heading font-bold text-white">
                                            {report.resumo.total_pas}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardContent className="p-4 flex items-center justify-center">
                                <Button
                                    onClick={exportToCSV}
                                    data-testid="btn-export-csv"
                                    className="bg-slate-700 hover:bg-slate-600 text-white rounded-sm"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Exportar CSV
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* ROOs List */}
                    {report.roos.length > 0 && (
                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardHeader className="border-b border-slate-800 p-4">
                                <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                                    ROOs no Período ({report.roos.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-800">
                                    {report.roos.map((roo) => (
                                        <div key={roo.id} className="p-4 hover:bg-slate-900/30">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-white font-medium">{roo.titulo}</p>
                                                    <p className="text-xs text-slate-500 font-mono">{roo.codigo}</p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className={`
                                                        px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                        ${roo.status === 'aberto' ? 'badge-warning' : ''}
                                                        ${roo.status === 'em_tratativa' ? 'badge-info' : ''}
                                                        ${roo.status === 'encerrado' ? 'badge-success' : ''}
                                                    `}>
                                                        {roo.status}
                                                    </span>
                                                    <span className="text-xs text-slate-500">
                                                        {roo.operador_nome}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Sinistros List */}
                    {report.sinistros.length > 0 && (
                        <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                            <CardHeader className="border-b border-slate-800 p-4">
                                <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                                    <Flame className="w-4 h-4 text-red-400" />
                                    Sinistros no Período ({report.sinistros.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-800">
                                    {report.sinistros.map((sin) => (
                                        <div key={sin.id} className="p-4 hover:bg-slate-900/30">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-white font-medium">{sin.titulo}</p>
                                                    <p className="text-xs text-slate-500 font-mono">{sin.codigo}</p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className={`
                                                        px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                        ${sin.status === 'aberto' ? 'badge-danger' : ''}
                                                        ${sin.status === 'em_tratativa' ? 'badge-warning' : ''}
                                                        ${sin.status === 'encerrado' ? 'badge-success' : ''}
                                                    `}>
                                                        {sin.status}
                                                    </span>
                                                    <span className="text-xs text-slate-500">
                                                        {sin.tipo}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
